import {
  HttpEvent,
  HttpHandler,
  HttpHeaders,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Injectable, Injector, inject } from '@angular/core';
import { EMPTY, Observable, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { TokenService } from '../pages/login/services/token.service';
import { SessionAuthService } from '../pages/login/services/session-auth.service';

@Injectable({
  providedIn: 'root',
})
export class InterceptorService implements HttpInterceptor {
  private readonly tokenService = inject(TokenService);
  private readonly injector = inject(Injector);

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    if (req.headers.has('X-Security-Token')) {
      return next.handle(req);
    }

    const isAuthPublic =
      req.url.includes('/auth/login') || req.url.includes('/auth/refresh');

    const sessionAuth = this.injector.get(SessionAuthService);
    const accessToken = sessionAuth.getAccessToken();
    const customHeaders: Record<string, string> = isAuthPublic
      ? {}
      : accessToken
        ? { Authorization: `Bearer ${accessToken}` }
        : this.tokenService.getToken()
          ? { 'session-id': this.tokenService.getToken() }
          : {};

    const isFormData =
      req.body instanceof FormData ||
      req.headers.get('Content-Type') === 'multipart/form-data';

    let headers = new HttpHeaders({
      ...(isFormData
        ? {}
        : {
            'Content-Type': 'application/json',
            accept: 'application/json',
          }),
      ...customHeaders,
    });
    const ifNoneMatch = req.headers.get('If-None-Match');
    if (ifNoneMatch) {
      headers = headers.set('If-None-Match', ifNoneMatch);
    }

    const reqClone = req.clone({ headers });

    return next.handle(reqClone).pipe(
      catchError(err => {
        if (err?.status !== 401 || isAuthPublic) {
          return throwError(() => err);
        }

        return sessionAuth.refresh().pipe(
          switchMap(() => {
            const token = sessionAuth.getAccessToken();
            const retryHeaders = token
              ? reqClone.headers.set('Authorization', `Bearer ${token}`)
              : reqClone.headers;
            return next.handle(req.clone({ headers: retryHeaders }));
          }),
          // ponytail: EMPTY evita snackbar del 401 de negocio; expireSession ya redirige.
          catchError(() => {
            sessionAuth.expireSession();
            return EMPTY;
          })
        );
      })
    );
  }
}
